/**
 * InfoModal
 * 
 * A styled modal for displaying informational messages (success, error, info).
 * Replaces browser alert() calls with a consistent, themed UI.
 */

import React from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle } from 'lucide-react';
import Modal from './Modal';

type InfoModalVariant = 'success' | 'error' | 'info' | 'warning';

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  variant?: InfoModalVariant;
}

const variantConfig: Record<InfoModalVariant, { icon: React.ElementType; iconColor: string }> = {
  success: { icon: CheckCircle, iconColor: 'text-emerald-400' },
  error: { icon: XCircle, iconColor: 'text-rose-400' },
  info: { icon: Info, iconColor: 'text-blue-400' },
  warning: { icon: AlertTriangle, iconColor: 'text-amber-400' },
};

const InfoModal: React.FC<InfoModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  variant = 'info',
}) => {
  const config = variantConfig[variant];
  const IconComponent = config.icon;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="text-center space-y-4">
        <IconComponent size={48} className={`mx-auto ${config.iconColor}`} />
        <p className="text-slate-300">{message}</p>
        <button
          onClick={onClose}
          className="w-full py-2 bg-slate-700 text-slate-200 rounded hover:bg-slate-600 font-bold transition-colors"
        >
          OK
        </button>
      </div>
    </Modal>
  );
};

export default InfoModal;
